import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { PrintWorkOrderMeta } from '../components/print/PrintWorkOrderMeta';
import { PrintCompletedWork, PrintTextSection } from '../components/print/PrintCompletedWork';
import { printCssString, printDate } from '../lib/workOrderPrint';
import '../components/print/workOrderPrint.css';

export default function WorkOrderPrintView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setData(null); setError('');
    axios.get(`${API}/work-orders/${encodeURIComponent(id)}/print`, { signal: controller.signal })
      .then(({ data: response }) => setData(response))
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.response?.status === 404 ? 'This work order could not be found.' : 'The work order could not be loaded. Please retry before printing.');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, reload]);
  useEffect(() => {
    const previous = document.title;
    if (data) document.title = `Work order ${data.work_order.wo_number || data.work_order.id} — ${data.work_order.title}`;
    return () => { document.title = previous; };
  }, [data]);
  const print = async () => {
    if (!data || printing) return;
    setPrinting(true);
    try { if (document.fonts?.ready) await document.fonts.ready; window.print(); }
    finally { setPrinting(false); }
  };
  const wo = data?.work_order;
  const label = wo ? `Work order ${wo.wo_number ? '#' + wo.wo_number : wo.id}` : '';
  return <main className="wo-print-view" data-testid="wo-print-preview-container">
    <div className="wo-print-toolbar" data-testid="wo-print-toolbar">
      <Button asChild variant="outline"><Link to={data?.equipment ? `/machinery/${data.equipment.id}?workOrder=${id}` : '/maintenance-history'} data-testid="wo-print-back-btn"><ArrowLeft size={16} className="mr-2" />{data?.equipment ? 'Back to equipment' : 'Back to history'}</Link></Button>
      <span className="wo-print-format" data-testid="wo-print-paper-size">US Letter · Portrait</span>
      <Button disabled={!data || loading || printing} onClick={print} data-testid="wo-print-trigger-btn"><Printer size={16} className="mr-2" />{printing ? 'Opening print dialog…' : 'Print / Save as PDF'}</Button>
    </div>
    {loading ? <p role="status" className="wo-print-state" data-testid="wo-print-loading-state">Loading work order…</p>
      : error ? <div role="alert" className="wo-print-state" data-testid="wo-print-error-state"><p>{error}</p><Button variant="outline" data-testid="wo-print-retry-btn" onClick={() => setReload((n) => n + 1)}><RefreshCw size={14} className="mr-2" /> Retry</Button></div>
      : data && <>
        <style>{`@page wo-document { @bottom-left { content: ${printCssString(label)}; } }`}</style>
        <article className="wo-print-sheet" data-testid="wo-print-document">
          <PrintWorkOrderMeta data={data} />
          <PrintTextSection title="Work instructions" text={wo.description} testId="wo-print-instructions" />
          {!data.legacy_completion_notes && <PrintTextSection title="Notes" text={wo.notes} testId="wo-print-notes" />}
          {wo.status === 'completed' ? <PrintCompletedWork data={data} /> : <PrintTextSection title="Parts noted on work order" text={wo.parts_used} testId="wo-print-parts-noted" />}
          <p className="wo-paper-asof" data-testid="wo-print-asof">Record retrieved {printDate(data.retrieved_at, true)}</p>
        </article>
      </>}
  </main>;
}